/**
 * Firebase Auth Module
 * Centraliza todas las operaciones de autenticación.
 * Provee: getAuth(), signInAnonymouslySafe(), signInWithEmail(), 
 * signUpWithEmail(), signInWithPopupGoogle(), signOutSafe(), onAuthChanged()
 */

import { 
  getAuth, 
  signInAnonymously, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  GoogleAuthProvider, 
  signInWithPopup,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { app } from './app.js';

// Singleton de auth para toda la aplicación
const auth = getAuth(app);

/**
 * Obtiene la instancia de Auth (para casos especiales)
 * @returns {import('firebase/auth').Auth}
 */
export const getAuthInstance = () => auth;

/**
 * Autenticación anónima con manejo de errores
 * @returns {Promise<import('firebase/auth').User>}
 */
export const signInAnonymouslySafe = async () => {
  try {
    const userCredential = await signInAnonymously(auth);
    console.log('[Auth] ✅ Usuario anónimo creado:', userCredential.user.uid);
    return userCredential.user;
  } catch (error) {
    console.error('[Auth] ❌ Error en signInAnonymously:', error.code, error.message);
    
    // Detectar error de configuración común
    if (JSON.stringify(error).includes('CONFIGURATION_NOT_FOUND') || error.code === 'auth/admin-restricted-operation') {
      console.warn('⚠️ IMPORTANTE: La autenticación no está habilitada en Firebase Console.');
      console.warn('👉 Pasos para solucionar:');
      console.warn('1. Ve a https://console.firebase.google.com/project/' + (auth.app.options.projectId || '_') + '/authentication/providers');
      console.warn('2. Haz clic en "Comenzar" si es la primera vez.');
      console.warn('3. En la pestaña "Sign-in method" (Proveedores de acceso), habilita "Anónimo".');
    }
    
    throw error;
  }
};

/**
 * Login con email y contraseña
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<import('firebase/auth').User>}
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('[Auth] ✅ Login con email exitoso:', userCredential.user.uid);
    return userCredential.user;
  } catch (error) {
    console.error('[Auth] ❌ Error en login email:', error.code);
    throw error;
  }
};

/**
 * Registro con email y contraseña
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<import('firebase/auth').User>}
 */
export const signUpWithEmail = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('[Auth] ✅ Registro con email exitoso:', userCredential.user.uid);
    return userCredential.user;
  } catch (error) {
    console.error('[Auth] ❌ Error en registro email:', error.code, error.message);
    throw error;
  }
};

/**
 * Login con Google via Popup
 * @returns {Promise<import('firebase/auth').User>}
 */
export const signInWithPopupGoogle = async () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: 'select_account'  // Forzar selección de cuenta
  });
  
  try {
    const result = await signInWithPopup(auth, provider);
    console.log('[Auth] ✅ Login con Google exitoso:', result.user.uid);
    return result.user;
  } catch (error) {
    console.error('[Auth] ❌ Error en login Google:', error.code, error.message);
    throw error;
  }
};

/**
 * Logout seguro con manejo de errores
 * @returns {Promise<void>}
 */
export const signOutSafe = async () => {
  try {
    await signOut(auth);
    console.log('[Auth] ✅ Sesión cerrada correctamente');
  } catch (error) {
    console.error('[Auth] ❌ Error al cerrar sesión:', error);
    throw error;
  }
};

/**
 * Suscripción a cambios de estado de autenticación
 * @param {function(import('firebase/auth').User|null): void} callback 
 * @returns {function(): void} Función de unsubscribe
 */
export const onAuthChanged = (callback) => {
  return onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('[Auth] ℹ️ Usuario activo:', user.uid, user.isAnonymous ? '(anónimo)' : '');
    } else {
      console.log('[Auth] ℹ️ No hay usuario activo');
    }
    callback(user);
  });
};

/**
 * Obtiene el usuario actual (síncrono, puede ser null)
 * @returns {import('firebase/auth').User|null}
 */
export const getCurrentUser = () => auth.currentUser;

export { auth };
